import { AuthorisedUser, AxeEventCreate, Collateral, CollateralBasket, OrderTerm, Organisation, TradingEntity } from 'api'
import { isNotEmpty, isNotNil } from 'utils/predicate'
import { AxeData } from './parseAxeSpreadsheet'
import { v4 as uuidv4 } from 'uuid'
import { Nullable } from 'utils'
import { isNil } from 'lodash'
import { Locale, Translation } from 'locale'

export class TradingEntityNotFoundError extends Error {
  constructor(readonly shortCode: string) {
    super(Locale.translate(Translation.SC_AXE_UPLOAD_TRADING_ENTITY_NOT_FOUND, { shortCode }))
  }
}

export const createMapAxeDataToAxeEventCreate = (
  getCollateralBasketByIsinOrId: (id: string) => CollateralBasket | null,
  getCollateralByIsinOrId: (id: string) => Collateral | null,
  getTradingEntityByShortNameAndOrganisationId: (organisationId: string, tradingEntityShortName: string) => TradingEntity | null,
  getOrganisationsByShortNames: (shortNames: string[]) => Organisation[] | null,
  authorisedUser: AuthorisedUser
) => (axeData: AxeData): AxeEventCreate => {
  const { initiatorTradingEntity } = axeData
  const tradingEntity = getTradingEntityByShortNameAndOrganisationId(authorisedUser.organisationId, initiatorTradingEntity)

  if (isNotEmpty(initiatorTradingEntity) && isNil(tradingEntity)) {
    throw new TradingEntityNotFoundError(initiatorTradingEntity)
  }

  const collateral = getCollateralByIsinOrId(axeData.isinBui)
  const collateralBasket = getCollateralBasketByIsinOrId(axeData.isinBui)
  const counterpartyOrganisationIds = getOrganisationsByShortNames(axeData.counterpartyOrganisations ?? [])?.map(
    organisation => organisation.id
  )
  const startDate = resolveDate(axeData.term, axeData.startDate)
  const endDate = resolveDate(axeData.term, axeData.endDate)

  return {
    initiatorTradingEntityId: tradingEntity?.id ?? '',
    collateralId: isNotNil(collateral) ? collateral.id : null,
    collateralBasketId: isNotNil(collateralBasket) ? collateralBasket.id : null,
    externalAxeId: null,
    eventType: 'AxeEventCreate',
    correlationId: uuidv4(),
    updatedValues: filterNonNullValues({
      RESPONDER_ORGANISATION_IDS: counterpartyOrganisationIds,
      DIRECTION: axeData.direction,
      NOTIONAL: axeData.notional,
      START_CONSIDERATION: axeData.startConsideration,
      RATE: axeData.rate,
      TERM: axeData.term,
      START_DATE: startDate,
      END_DATE: endDate,
      REFERENCE_RATE: axeData.referenceRate,
      REPO_TYPE: axeData.repoType
    })
  }
}

const filterNonNullValues = (updatedValues: AxeEventCreate['updatedValues']) =>
  Object.entries(updatedValues).reduce(
    (filteredUpdatedValues, [key, value]) =>
      isNil(value)
        ? filteredUpdatedValues
        : {
            ...filteredUpdatedValues,
            [key]: value
          },
    {}
  )

const resolveDate = (term: Nullable<OrderTerm>, date: Nullable<Date>) => {
  if (isNil(term) || term === OrderTerm.CUSTOM || term === OrderTerm.CUSTOM_O) {
    return date
  }
  return null
}
